import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import iziToast from "izitoast";
import { setUserTokenAction } from "../redux/userSlice";
import { loginCompany } from "actions/companyActions";
import { getActiveDistributor } from "actions/distributorActions";
import { useAuth } from "AuthContext";
import "./LoginPage.css";

const Svg = ({ d, size = 18, fill = "none", stroke = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill={fill}
    stroke={stroke}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: "0 0 auto" }}
  >
    {d}
  </svg>
);
const IconMail = (p) => (
  <Svg
    {...p}
    d={
      <>
        <rect x="3" y="5" width="14" height="10" rx="1.5" />
        <path d="m3.5 6 6.5 5 6.5-5" />
      </>
    }
  />
);
const IconLock = (p) => (
  <Svg
    {...p}
    d={
      <>
        <rect x="4.5" y="9" width="11" height="8" rx="1.5" />
        <path d="M7 9V6.5a3 3 0 1 1 6 0V9" />
      </>
    }
  />
);
const IconEye = (p) => (
  <Svg
    {...p}
    d={
      <>
        <path d="M2.5 10S5 4.5 10 4.5 17.5 10 17.5 10 15 15.5 10 15.5 2.5 10 2.5 10Z" />
        <circle cx="10" cy="10" r="2.5" />
      </>
    }
  />
);
const IconEyeOff = (p) => (
  <Svg
    {...p}
    d={
      <>
        <path d="M4 4.5 16 16M7.2 7.4A8.7 8.7 0 0 0 2.5 10s2.5 5.5 7.5 5.5a8.3 8.3 0 0 0 3.5-.78M12.5 12.5a2.5 2.5 0 0 1-3.5-3.5M9.2 5.6A8.9 8.9 0 0 1 10 4.5C15 4.5 17.5 10 17.5 10a13.7 13.7 0 0 1-2.4 2.9" />
      </>
    }
  />
);
const IconAlert = (p) => (
  <Svg
    {...p}
    size={14}
    d={
      <>
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6.5v4M10 13.25v.01" />
      </>
    }
  />
);
const IconCheck = (p) => <Svg {...p} d={<path d="m4.5 10.5 3.5 3.5 7.5-8" />} />;
const IconArrow = (p) => <Svg {...p} d={<path d="M4 10h12m-4-4 4 4-4 4" />} />;

const Glyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    fill="#24D0C0"
    aria-hidden="true"
  >
    <path d="M100 0C44.7 0 0 44.7 0 100s44.7 100 100 100 100-44.7 100-100S155.3 0 100 0Zm0 134.6c-19.1 0-34.6-15.6-34.6-34.6S80.9 65.4 100 65.4s34.6 15.6 34.6 34.6-15.5 34.6-34.6 34.6Zm53.3-34.6c0-29.5-23.9-53.4-53.3-53.4-19.6 0-36.8 10.7-46 26.5 5.6-27.8 30.2-48.7 59.5-48.7 33.5 0 60.7 27.3 60.7 60.9 0 27.4-18.1 50.6-43 58.2 13.4-9.7 22.1-25.5 22.1-43.5Zm-53.3 81.7c-44.7 0-81.1-36.5-81.1-81.7 0-27.2 13.4-51.3 33.8-66.1-11.6 13.9-18.6 31.7-18.6 51.2 0 44.1 35.5 79.7 79.4 79.7 22.2 0 42.4-9.2 56.7-23.9-14.1 24.2-40.3 40.8-70.2 40.8Z" />
  </svg>
);

// Semi360 brand wordmark — inline SVG so the lettering adapts to the theme
// (fill = currentColor → white on the dark cover, ink on light) while the 360
// globe mark keeps its teal.
const Semi360Logo = ({ className, title = "Semi360" }) => (
  <svg
    className={className}
    viewBox="0 0 1041 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title}
  >
    <g clipPath="url(#semi360_logo_clip)">
      <path fill="currentColor" d="M584.551 91.6209C604.962 90.4206 617.926 75.9404 617.926 60.029C617.926 41.4555 606.402 29.6374 587.907 29.6374C572.782 29.6374 559.573 37.5931 557.888 59.0595H536.756C538.672 22.6358 564.86 10.5868 588.872 10.5868C618.646 10.5868 639.302 30.1144 639.302 56.8898C639.302 78.3563 626.814 90.6515 613.13 97.1606V97.8839C631.855 102.7 645.784 117.904 645.784 142.74C645.784 175.533 622.002 197.969 588.627 197.969C555.252 197.969 533.155 178.441 530.995 146.603H553.091C555.007 170.239 572.307 178.441 588.872 178.441C608.563 178.441 622.967 165.423 622.967 142.263C622.967 119.104 605.682 106.332 584.536 106.578V91.6209H584.551Z"/>
      <path fill="currentColor" d="M651.069 135.262C651.314 121.52 655.635 107.532 665.243 92.098L710.632 13.4799H737.05L696.228 79.8029L696.703 80.0491C704.15 75.9558 713.988 73.2937 723.366 73.2937C757.706 73.2937 782.929 96.9299 782.929 134.062C782.929 171.193 754.826 197.969 716.654 197.969C678.483 197.969 650.609 172.886 651.084 135.262H651.069ZM760.342 135.262C760.342 108.733 742.812 91.6056 718.079 91.6056C691.661 91.6056 673.886 108.24 673.886 135.985C673.886 162.037 692.857 178.918 717.114 178.918C741.371 178.918 760.342 161.56 760.342 135.262Z"/>
      <path fill="currentColor" d="M50.1391 200C37.9568 200 27.6439 197.553 19.1546 192.644C10.6653 187.736 4.27531 181.842 0 174.948L20.8249 156.359C29.2223 166.177 39.5045 171.086 51.6868 171.086C56.8356 171.086 60.8504 170.008 63.7772 167.854C66.6887 165.7 68.1445 162.73 68.1445 158.944C68.1445 155.159 65.9532 152.189 61.5859 149.527C57.2187 146.865 51.9014 144.449 45.6493 142.294C39.3972 140.14 33.1758 137.555 27.0004 134.554C20.8249 131.538 15.5536 126.983 11.1863 120.874C6.81904 114.765 4.62776 107.225 4.62776 98.2842C4.62776 86.066 9.25551 76.3407 18.511 69.1083C27.7665 61.8758 39.4279 58.2596 53.4797 58.2596C74.3965 58.2596 89.7356 66.2615 99.5121 82.2805L76.1128 99.8384C69.2478 91.2364 61.2028 86.9278 51.9473 86.9278C47.8406 86.9278 44.4847 87.9126 41.9256 89.8977C39.3512 91.8828 38.0641 94.4987 38.0641 97.7764C38.0641 101.562 40.2553 104.747 44.6226 107.332C48.9899 109.918 54.3072 112.195 60.5592 114.18C66.8113 116.165 73.0787 118.566 79.3308 121.413C85.5828 124.259 90.9002 128.691 95.2674 134.708C99.6347 140.74 101.826 148.219 101.826 157.175C101.826 170.085 96.9377 180.457 87.1612 188.29C77.3847 196.122 65.0491 200.046 50.1391 200.046V200Z"/>
      <path fill="currentColor" d="M108.521 129.507C108.521 109.025 114.727 92.0213 127.17 78.5105C139.597 64.9996 155.335 58.2442 174.351 58.2442C194.916 58.2442 210.944 64.6611 222.437 77.4794C233.915 90.2978 239.661 107.917 239.661 130.292C239.661 133.739 239.569 136.324 239.401 138.032H144.777C145.114 147.157 148.546 154.605 155.059 160.375C161.572 166.146 169.8 169.024 179.745 169.024C193.966 169.024 204.861 163.515 212.4 152.497L235.799 170.316C232.03 177.21 225.333 183.873 215.741 190.321C206.133 196.784 193.966 200 179.224 200C157.97 200 140.869 193.414 127.921 180.242C114.972 167.069 108.506 150.158 108.506 129.507H108.521ZM145.803 114.78H203.666C203.313 106.517 200.494 99.9 195.176 94.8988C189.859 89.9131 183.086 87.4048 174.857 87.4048C166.628 87.4048 159.947 89.99 154.798 95.1451C149.65 100.3 146.662 106.855 145.803 114.765V114.78Z"/>
      <path fill="currentColor" d="M446.408 107.563V196.645H410.918V115.827C410.918 107.917 409.034 101.67 405.264 97.0993C401.494 92.5445 396.345 90.2516 389.833 90.2516C383.32 90.2516 378.049 92.5291 374.019 97.0993C369.989 101.654 367.981 107.994 367.981 116.073V196.645H332.492V114.534C332.492 106.963 330.653 101.023 326.96 96.7146C323.267 92.4213 318.179 90.2516 311.651 90.2516C305.124 90.2516 299.868 92.5291 295.837 97.0993C291.807 101.654 289.8 107.902 289.8 115.827V196.645H254.31V61.5989H289.034V77.3563C291.777 72.0166 296.19 67.5079 302.273 63.7994C308.357 60.1062 315.605 58.2442 324.002 58.2442C340.981 58.2442 353.225 64.9535 360.779 78.3873C369.345 64.9535 382.631 58.2442 400.636 58.2442C415.546 58.2442 426.901 62.7222 434.716 71.6781C442.516 80.634 446.408 92.5906 446.408 107.579V107.563Z"/>
      <path fill="currentColor" d="M470.836 43.6409C466.882 39.763 464.921 34.9004 464.921 29.0529C464.921 23.2054 466.898 18.3427 470.836 14.4649C474.774 10.5871 479.662 8.64816 485.485 8.64816C491.308 8.64816 496.457 10.5871 500.395 14.4649C504.333 18.3427 506.31 23.2054 506.31 29.0529C506.31 34.9004 504.333 39.763 500.395 43.6409C496.442 47.5187 491.477 49.4576 485.485 49.4576C479.494 49.4576 474.774 47.5187 470.836 43.6409ZM468.001 196.645V61.5988H503.491V196.645H468.001Z"/>
      <path fill="#1FD0C0" d="M1013.33 178.241H997.405L994.509 186.659H984.625L1000.49 144.51H1010.74L1026.6 186.659H1016.22L1013.33 178.241ZM1010.37 169.824L1005.36 155.236L1000.29 169.824H1010.35H1010.37Z"/>
      <path fill="#1FD0C0" d="M1031.72 150.45C1030.63 149.419 1030.09 148.096 1030.09 146.572C1030.09 145.049 1030.63 143.726 1031.72 142.695C1032.74 141.664 1034.02 141.125 1035.52 141.125C1037.02 141.125 1038.35 141.664 1039.44 142.695C1040.47 143.726 1041 145.064 1041 146.572C1041 148.08 1040.47 149.419 1039.44 150.45C1038.35 151.481 1037.03 152.02 1035.52 152.02C1034 152.02 1032.74 151.481 1031.72 150.45ZM1030.94 155.113H1040.1V186.659H1030.94V155.113Z"/>
      <path fill="#1FD0C0" d="M892.898 0C838.622 0 794.627 44.1794 794.627 98.6843C794.627 153.189 838.622 197.353 892.898 197.353C947.175 197.353 991.169 153.174 991.169 98.6843C991.169 44.1948 947.175 0 892.898 0ZM892.898 132.923C874.096 132.923 858.803 117.566 858.803 98.6843C858.803 79.803 874.096 64.4303 892.898 64.4303C911.7 64.4303 926.993 79.803 926.993 98.6843C926.993 117.566 911.7 132.923 892.898 132.923ZM945.382 98.6843C945.382 69.5699 921.89 45.9645 892.898 45.9645C873.56 45.9645 856.657 56.4746 847.555 72.1089C853.087 44.718 877.268 24.0363 906.138 24.0363C939.084 24.0363 965.9 50.9656 965.9 84.0502C965.9 111.056 948.048 133.939 923.576 141.448C936.785 131.877 945.382 116.288 945.382 98.6843ZM892.898 178.887C848.858 178.887 813.016 142.91 813.016 98.6843C813.016 71.8935 826.163 48.1188 846.329 33.5462C834.883 47.1955 827.987 64.815 827.987 84.0502C827.987 127.399 862.971 162.53 906.138 162.53C928.02 162.53 947.788 153.512 961.977 138.955C948.109 162.822 922.335 178.887 892.898 178.887Z"/>
    </g>
    <defs>
      <clipPath id="semi360_logo_clip">
        <rect width="1041" height="200" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const validateEmail = (v) => {
  const t = (v || "").trim();
  if (!t) return "Enter your work email.";
  if (!EMAIL_RE.test(t)) return "That doesn't look like a valid email.";
  return "";
};
const validatePassword = (v) => {
  if (!v) return "Enter your password.";
  return "";
};

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setToken } = useAuth();

  const [theme] = useState("dark");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [emailFocus, setEmailFocus] = useState(false);
  const [pwFocus, setPwFocus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const emailRef = useRef(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage("");
  };

  const emailErr = touched.email ? validateEmail(formData.email) : "";
  const pwErr = touched.password ? validatePassword(formData.password) : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setErrorMessage("");

    if (validateEmail(formData.email) || validatePassword(formData.password)) return;

    try {
      setIsLoading(true);
      const response = await loginCompany(formData);
      if (response.status) {
        iziToast.success({
          title: "Success",
          message: response.message,
          position: "topCenter",
        });
        localStorage.setItem("token", response.data?.token);
        dispatch(
          setUserTokenAction({ user: response.data?.user, token: response.data?.token })
        );
        setToken(response.data?.token);
        setSuccess(true);

        // Route new customers (no active distributor yet) through onboarding;
        // everyone else goes straight to the dashboard. The token is already in
        // localStorage above, so this call is authenticated.
        let hasActiveDistributor = false;
        try {
          const distRes = await getActiveDistributor();
          hasActiveDistributor =
            distRes?.status && distRes?.data && Object.keys(distRes.data).length > 0;
        } catch (e) {
          hasActiveDistributor = false;
        }

        setTimeout(() => {
          navigate(hasActiveDistributor ? "/dashboard" : "/onboarding");
        }, 600);
      } else {
        setErrorMessage(response.message);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  const isDark = theme === "dark";

  return (
    <div className={`sb-login-stage ${isDark ? "sb-dark" : "sb-light"}`} data-screen-label="Login">
      <Glyph className="sb-glyph" />

      <div className="sb-shell">
        <div className="sb-brand-lockup">
          <Semi360Logo className="sb-brand-logo" />
        </div>

        <form className="sb-card" onSubmit={handleSubmit} noValidate>
          <p className="sb-eyebrow">Buyer portal</p>
          <h1>Sign in</h1>
          <p className="sb-lede">
            Upload a BOM and benchmark prices across Mouser, Digi-Key, Element 14, and 40+
            distributors in seconds.
          </p>

          {errorMessage && (
            <div className="sb-server-error" role="alert">
              <IconAlert /> {errorMessage}
            </div>
          )}

          <div className="sb-field">
            <label htmlFor="login-email">Work email</label>
            <div
              className={`sb-input-wrap ${emailFocus ? "sb-focused" : ""} ${
                emailErr ? "sb-error" : ""
              }`}
            >
              <span className="sb-lead">
                <IconMail />
              </span>
              <input
                ref={emailRef}
                id="login-email"
                type="email"
                name="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setEmailFocus(true)}
                onBlur={() => {
                  setEmailFocus(false);
                  setTouched((t) => ({ ...t, email: true }));
                }}
                aria-invalid={!!emailErr}
              />
            </div>
            {emailErr && (
              <span className="sb-error-text">
                <IconAlert /> {emailErr}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="login-password">Password</label>
            <div
              className={`sb-input-wrap ${pwFocus ? "sb-focused" : ""} ${
                pwErr ? "sb-error" : ""
              }`}
            >
              <span className="sb-lead">
                <IconLock />
              </span>
              <input
                id="login-password"
                type={showPw ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setPwFocus(true)}
                onBlur={() => {
                  setPwFocus(false);
                  setTouched((t) => ({ ...t, password: true }));
                }}
                aria-invalid={!!pwErr}
              />
              <button
                type="button"
                className="sb-trail"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPw ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {pwErr && (
              <span className="sb-error-text">
                <IconAlert /> {pwErr}
              </span>
            )}
          </div>

          <div className="sb-row-between">
            <label className="sb-checkbox">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="sb-box">{remember && <IconCheck size={12} />}</span>
              Remember me
            </label>
            <Link className="sb-link" to="/forgot-password">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className={`sb-btn sb-btn-primary ${success ? "sb-success" : ""}`}
            disabled={isLoading || success}
          >
            {success ? (
              <>
                <IconCheck size={18} /> Signed in
              </>
            ) : isLoading ? (
              <>
                <span className="sb-spinner" /> Signing you in…
              </>
            ) : (
              <>
                Sign in <IconArrow size={16} />
              </>
            )}
          </button>

          <div className="sb-signup-line">
            New to Semi360?{" "}
            <Link to="/register" className="sb-link">
              Create an account
            </Link>
          </div>
        </form>

        <div className="sb-trust">
          <span className="sb-trust-label">Compares</span>
          <span className="sb-trust-logo">Mouser</span>
          <span className="sb-trust-logo">Digi-Key</span>
          <span className="sb-trust-logo">Element 14</span>
          <span className="sb-trust-logo">Arrow</span>
          <span className="sb-trust-logo">+ 40 more</span>
        </div>

        <div className="sb-legal">
          © {new Date().getFullYear()} Semi360 ·{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            Privacy
          </a>
          <span className="sb-sep">·</span>
          <a href="#" onClick={(e) => e.preventDefault()}>
            Terms
          </a>
          <span className="sb-sep">·</span>
          <a href="#" onClick={(e) => e.preventDefault()}>
            Status
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
